import { Utils } from "@/plugins/utils"
import { createVdpChildrenCacheManager } from "@/services/oryxplaceOrders/VdpChildrenCacheManager.js"
import {
	createVdpItems,
	projectVdpItem,
	unpinVdpCommission,
	unpinVdpPrice,
	vdpNormalizer,
	vdpUpdater,
} from "@/services/oryxplaceOrders/VdpItemModel.js"
import Enums from "../../plugins/Enums"

const childrenManager = createVdpChildrenCacheManager()

function cloneMap(m) {
	return m instanceof Map ? new Map(m) : new Map()
}

function emptyMap() {
	return new Map()
}

function getProductCode(vdp) {
	const it = vdpNormalizer(vdp)
	return !!it.vdpinsumo ? it.vdpinsumo : it.vdprecip
}

function hasActivePriceRange(raw) {
	return Number(raw?.in1precmin ?? 0) > 0 || Number(raw?.in1precmax ?? 0) > 0
}

function getVdqContext(rootGetters) {
	const veacliente = rootGetters["vdq/vdqcliente"]
	const veavend = rootGetters["vdq/vdqvend"]

	return { veacliente: veacliente, veavend: veavend }
}

const commissionInFlight = new Map()
export default {
	namespaced: true,

	state: {
		soldItems: emptyMap(),
		returnedItems: emptyMap(),

		specialPrices: null,
		comissions: null,
		priceList: null,

		loadingOrderItems: false,
		loadingComissions: false,
		loadingPriceList: false,
		loadingSpecialPrices: false,

		mapEpoch: 0,
		lastMapRefreshAt: null,
	},

	getters: {
		hasOrderItems: state => {
			return state.soldItems.size + state.returnedItems.size > 0
		},

		childrenByParentKey: state => {
			void state.mapEpoch
			return childrenManager.getChildrenByParentKey({
				soldMap: state.soldItems,
				returnedMap: state.returnedItems,
			})
		},

		soldItemsList: (state, getters) => {
			const ctx = {
				priceList: state.priceList,
				specialPrices: state.specialPrices,
				comissions: state.comissions,
			}

			const childrenByPk = getters.childrenByParentKey
			const items = Array.from(state.soldItems.values()).map(raw => {
				const item = projectVdpItem(raw, ctx)
				const children = childrenByPk.get(item.parentKey) || []
				return {
					...item,
					children: children,
				}
			})
			return items
		},

		returnedItemsList: (state, getters) => {
			const ctx = {
				priceList: state.priceList,
				specialPrices: state.specialPrices,
				comissions: state.comissions,
			}

			const childrenByPk = getters.childrenByParentKey

			return Array.from(state.returnedItems.values()).map(raw => {
				const item = projectVdpItem(raw, ctx)
				const children = childrenByPk.get(item.parentKey) || []
				return {
					...item,
					children: children.map(c => ({ ...c })),
				}
			})
		},
	},

	mutations: {
		setLoadingTables(state, value) {
			state.loadingOrderItems = !!value
		},

		setLoadingComissions(state, value) {
			state.loadingComissions = !!value
		},
		setLoadingPriceList(state, value) {
			state.loadingPriceList = !!value
		},
		setLoadingSpecialPrices(state, value) {
			state.loadingSpecialPrices = !!value
		},

		initForOrder(state) {
			state.soldItems = emptyMap()
			state.returnedItems = emptyMap()

			state.specialPrices = null
			state.priceList = null
			state.comissions = null

			state.mapEpoch++
			state.lastMapRefreshAt = Date.now()
		},

		setSpecialPrices(state, prices) {
			state.specialPrices = prices || null
		},

		setPriceList(state, list) {
			state.priceList = list || null
		},

		setComissions(state, comissions) {
			if (!Array.isArray(comissions)) {
				state.comissions = null
				return
			}

			const prev = state.comissions && typeof state.comissions === "object" ? state.comissions : {}

			const next = { ...prev }

			for (const c of comissions) {
				if (!c?.veaproduto) continue
				next[c.veaproduto] = {
					veacomis: c.veacomis,
					enumValue: c.enumValue,
					enumMessage: c.enumMessage ?? "",
				}
			}

			state.comissions = next
		},

		replaceBucketMap(state, { bucket, map }) {
			state[bucket] = map instanceof Map ? map : emptyMap()
			state.mapEpoch++
			state.lastMapRefreshAt = Date.now()
		},

		replaceAllMaps(state, { soldMap, returnedMap }) {
			state.soldItems = soldMap instanceof Map ? soldMap : emptyMap()
			state.returnedItems = returnedMap instanceof Map ? returnedMap : emptyMap()
			state.mapEpoch++
			state.lastMapRefreshAt = Date.now()
		},

		refreshMaps(state) {
			state.soldItems = cloneMap(state.soldItems)
			state.returnedItems = cloneMap(state.returnedItems)
			state.mapEpoch++
			state.lastMapRefreshAt = Date.now()
		},

		setItemsFromApi(state, { soldItems = [], returnedItems = [], priceRangeActive = false } = {}) {
			const normalize = raw => {
				const src =
					priceRangeActive && hasActivePriceRange(raw) ? raw : { ...raw, in1precmin: undefined, in1precmax: undefined }
				return vdpNormalizer({ ...src, priceSource: "external", commissionSource: "external" })
			}

			const soldMap = new Map()
			const returnedMap = new Map()

			for (const raw of soldItems || []) {
				const item = normalize(raw)
				soldMap.set(item.localKey, item)
			}

			for (const raw of returnedItems || []) {
				const item = normalize(raw)
				returnedMap.set(item.localKey, item)
			}

			state.soldItems = soldMap
			state.returnedItems = returnedMap
			state.mapEpoch++
			state.lastMapRefreshAt = Date.now()
		},

		upsertItem(state, { item }) {
			const normalized = vdpNormalizer(item)
			const bucket = normalized.bucket
			const key = normalized.localKey

			const next = cloneMap(state[bucket])
			next.set(key, normalized)

			state[bucket] = next
			state.mapEpoch++
			state.lastMapRefreshAt = Date.now()
		},

		deleteItem(state, { item }) {
			const normalized = vdpNormalizer(item)
			const bucket = normalized.bucket
			const key = normalized.localKey

			const next = cloneMap(state[bucket])
			next.delete(key)

			state[bucket] = next
			state.mapEpoch++
			state.lastMapRefreshAt = Date.now()
		},
	},

	actions: {
		initForOrder({ commit }) {
			commit("initForOrder")
		},

		async ensureCommissionsForProducts(
			{ state, dispatch, commit, rootGetters },
			{ products = [], concurrency = 6, setLoading = false } = {},
		) {
			const { veacliente, veavend } = getVdqContext(rootGetters)

			const unique = new Set()
			for (const p of products || []) {
				const code = p
				if (code) unique.add(code)
			}

			const missing = []
			for (const code of unique) {
				if (state.comissions && state.comissions[code]) continue
				missing.push(code)
			}

			if (!missing.length) return []

			if (setLoading) commit("setLoadingComissions", true)
			try {
				const taskFns = missing.map(code => async () => {
					const queryKey = `${veacliente}|${veavend}|${code}`

					if (commissionInFlight.has(queryKey)) {
						return commissionInFlight.get(queryKey)
					}

					const p = (async () => {
						const data = await dispatch(
							"commission/findProduct",
							{ veacliente, veavend, veaproduto: code, silent: true },
							{ root: true },
						)

						if (!data) return null

						const enumValue = Number(data.enumValue ?? 0)
						return {
							veaproduto: data.veaproduto ?? code,
							veacomis: data.veacomis ?? "0",
							enumValue,
							enumMessage: data.enumMessage ?? Enums.CommissionGridMessage?.[enumValue]?.name ?? "",
						}
					})().finally(() => {
						commissionInFlight.delete(queryKey)
					})

					commissionInFlight.set(queryKey, p)
					return p
				})

				const settled = await Utils.runPool(taskFns, Math.max(1, Number(concurrency) || 6))

				const list = []
				for (const s of settled) {
					if (s.status === "fulfilled" && s.value) list.push(s.value)
				}
				if (list.length) commit("setComissions", list)
				return list
			} finally {
				if (setLoading) commit("setLoadingComissions", false)
			}
		},

		async syncMissingCommissionsFromState(
			{ state, dispatch, commit },
			{ concurrency = 6, setLoading = true, reset = false } = {},
		) {
			if (reset) commit("setComissions", null)

			const products = new Set()
			for (const item of state.soldItems.values()) {
				const code = getProductCode(item)
				if (code) products.add(code)
			}
			for (const item of state.returnedItems.values()) {
				const code = getProductCode(item)
				if (code) products.add(code)
			}

			return dispatch("ensureCommissionsForProducts", {
				products: Array.from(products),
				concurrency,
				setLoading,
			})
		},

		async addItem({ state, commit, dispatch }, rawItem) {
			const item = vdpNormalizer(rawItem)
			const bucket = item.bucket
			const key = item.localKey

			const before = state[bucket].get(key) || null
			if (before) {
				const newQty = Number(before.vdpqtde) + Number(item.vdpqtde)
				return dispatch("updateVdpFields", { item: before, changes: { vdpqtde: newQty } })
			}

			commit("upsertItem", { item })

			const produto = getProductCode(item)
			if (produto) {
				await dispatch("ensureCommissionsForProducts", {
					products: [produto],
					concurrency: 3,
					setLoading: false,
				})
			}

			const after = state[bucket].get(key) || item
			return vdpNormalizer(after)
		},

		async addItems({ state, commit, dispatch }, rawItems = []) {
			const soldNext = cloneMap(state.soldItems)
			const returnedNext = cloneMap(state.returnedItems)

			const insertedProducts = new Set()
			const resultMeta = []

			for (const raw of rawItems || []) {
				const item = vdpNormalizer(raw)
				const bucket = item.bucket
				const key = item.localKey

				const nextMap = bucket === "returnedItems" ? returnedNext : soldNext
				const before = nextMap.get(key) || null

				if (before) {
					const newQty = Number(before.vdpqtde) + Number(item.vdpqtde)
					const after = vdpUpdater(before, { vdpqtde: newQty })
					nextMap.set(key, after)
					resultMeta.push({ bucket, key, type: "merged" })
				} else {
					nextMap.set(key, item)
					resultMeta.push({ bucket, key, type: "inserted" })
					const produto = getProductCode(item)
					if (produto) insertedProducts.add(produto)
				}
			}

			commit("replaceAllMaps", { soldMap: soldNext, returnedMap: returnedNext })

			if (insertedProducts.size) {
				await dispatch("ensureCommissionsForProducts", {
					products: Array.from(insertedProducts),
					concurrency: 6,
					setLoading: false,
				})
			}

			const results = []
			for (const meta of resultMeta) {
				const fallback = { vdpident: meta.bucket === "returnedItems" ? 1 : 0, vdpinsumo: "", vdprecip: "" }
				const stored = state[meta.bucket].get(meta.key) || fallback
				results.push({
					type: meta.type,
					item: vdpNormalizer(stored),
				})
			}
			return results
		},

		refreshMaps({ commit }) {
			commit("refreshMaps")
		},

		removeItem({ state, commit }, maybeItem) {
			const item = vdpNormalizer(maybeItem)
			const bucket = item.bucket
			const key = item.localKey

			const before = state[bucket].get(key) || null
			if (!before) return

			commit("deleteItem", { item: before })
		},

		updateVdpFields({ state, commit }, { item: maybeItem, changes }) {
			const item = vdpNormalizer(maybeItem)
			const bucket = item.bucket
			const key = item.localKey

			const before = state[bucket].get(key) || null
			if (!before) return

			const afterItem = vdpUpdater(before, changes)
			commit("upsertItem", { item: afterItem })

			const after = state[afterItem.bucket].get(afterItem.localKey) || afterItem
			return vdpNormalizer(after)
		},

		setDerivedContextLayer({ commit }, { priceList, specialPrices, comissions } = {}) {
			if (priceList !== undefined) commit("setPriceList", priceList)
			if (specialPrices !== undefined) commit("setSpecialPrices", specialPrices)
			if (comissions !== undefined) commit("setComissions", comissions)
		},

		async fetchByVdq({ commit, dispatch, rootGetters }, vdqvisita) {
			commit("setLoadingTables", true)
			try {
				const response = await this._vm.$axios.get("/Oryxplace/OryxplaceOrder/GetAllItemsByVdq", {
					params: { vdqvisita },
				})

				const data = Utils.ValidateAxiosResponse(response.data)
				const priceRangeActive = rootGetters["lxd/getLxd"]?.lxdcnprec === true

				commit("setItemsFromApi", {
					soldItems: data.soldItems || [],
					returnedItems: data.returnedItems || [],
					priceRangeActive,
				})

				await dispatch("syncMissingCommissionsFromState", {
					concurrency: 6,
					setLoading: true,
					reset: true,
				})

				return data
			} finally {
				commit("setLoadingTables", false)
			}
		},

		async hydrateFromOrderPayload({ commit, dispatch, rootGetters }, itemsPayload) {
			const priceRangeActive = rootGetters["lxd/getLxd"]?.lxdcnprec === true

			commit("setItemsFromApi", {
				soldItems: itemsPayload?.soldItems || [],
				returnedItems: itemsPayload?.returnedItems || [],
				priceRangeActive,
			})

			await dispatch("syncMissingCommissionsFromState", {
				concurrency: 6,
				setLoading: true,
				reset: true,
			})
		},

		async createVdpItems({ dispatch, rootGetters }, { key, in1insumo, in1recip, amount = 1, related = false }) {
			const priceRangeActive = rootGetters["lxd/getLxd"]?.lxdcnprec === true
			const fetchIn1ByCode = code => dispatch("in1/findByCode", { in1insumo: code }, { root: true })
			const fetchCylinderFullInfo = async code => {
				const recip = await dispatch(
					"in1/findCylinderFullInfo",
					{ in1insumo: code, ignoreErrorDialog: true },
					{ root: true },
				)
				return recip || null
			}

			const out = await createVdpItems({
				key,
				in1insumo,
				in1recip,
				amount,
				related,
				priceRangeActive,
				fetchIn1ByCode,
				fetchCylinderFullInfo,
				vdpNormalizer,
			})

			return dispatch("addItems", out)
		},

		async applyDraftOps({ dispatch }, { vdp, ops, showErrorDialog = true }) {
			const changes = {}
			for (const op of ops || []) changes[op.path] = op.to

			try {
				return await dispatch("updateVdpFields", { item: vdp, changes, showErrorDialog })
			} catch (error) {
				if (showErrorDialog) {
					await dispatch(
						"parameters/openDialog",
						{ titleText: "Erro", titleColor: "red", text: error.message },
						{ root: true },
					)
				}
				throw error
			}
		},

		batchUpdateVdpFields({ state, commit }, { targets = [], changes = {} }) {
			if (!Array.isArray(targets) || !targets.length) return []

			const soldNext = new Map(state.soldItems)
			const returnedNext = new Map(state.returnedItems)

			const results = []

			for (const maybeItem of targets) {
				const item = vdpNormalizer(maybeItem)
				const bucket = item.bucket
				const key = item.localKey

				const nextMap = bucket === "returnedItems" ? returnedNext : soldNext
				const before = nextMap.get(key)

				if (!before) continue

				const after = vdpUpdater(before, changes)
				nextMap.set(key, after)
				results.push(vdpNormalizer(after))
			}

			commit("replaceAllMaps", {
				soldMap: soldNext,
				returnedMap: returnedNext,
			})

			return results
		},

		unpinPrice({ commit }, item) {
			const next = unpinVdpPrice(item)
			return commit("upsertItem", { item: next })
		},

		unpinCommission({ commit }, item) {
			const next = unpinVdpCommission(item)
			return commit("upsertItem", { item: next })
		},
	},
}
